import { useEffect, useRef } from 'react'
import { useQuery } from '@tanstack/react-query'

import { Alert, InlineLoader } from '../../components/ui'
import { keys } from '../../api/keys'
import { fetchMessages } from '../../api/threads'
import type { Message } from '../../api/threads'
import { t } from '../../i18n'
import { shortTime } from '../../format'
import { directionLabel } from '../../labels'

/**
 * The conversation as it happened.
 *
 * Who wrote each message is a label rather than only a position, because the
 * distinction that matters here is not left-versus-right: the owner answering
 * from their phone and this system sending an approved draft both leave the
 * account as outgoing, and a manager reading the thread has to be able to tell
 * which of them said what (invariant 6).
 */
export function Conversation({
  threadId,
  highlighted,
  highlightCount = 0,
}: {
  readonly threadId: string
  readonly highlighted: string | null
  readonly highlightCount?: number
}) {
  const messages = useQuery({
    queryKey: keys.messages(threadId),
    queryFn: ({ signal }) => fetchMessages(threadId, signal),
  })

  const list = useRef<HTMLOListElement>(null)
  const count = messages.data?.length ?? 0

  // The server sends the conversation oldest first, which is the order it was
  // had; the part worth reading is the end. Jumping to the bottom is skipped
  // while a piece of evidence is highlighted, because that scroll was asked for
  // deliberately and overriding it would make the evidence links useless.
  useEffect(() => {
    if (highlighted !== null) return
    const element = list.current
    if (element !== null) element.scrollTop = element.scrollHeight
  }, [count, highlighted, threadId])

  if (messages.isPending) return <InlineLoader />
  if (messages.isError) return <Alert tone="error">{t('thread.messagesFailed')}</Alert>
  if (messages.data.length === 0) {
    return <p className="conversation__empty">{t('thread.messagesEmpty')}</p>
  }

  return (
    <ol className="conversation" ref={list}>
      {messages.data.map((message) => (
        <MessageRow
          key={message.id}
          message={message}
          highlighted={message.id === highlighted}
          highlightCount={highlightCount}
        />
      ))}
    </ol>
  )
}

function MessageRow({
  message,
  highlighted,
  highlightCount,
}: {
  readonly message: Message
  readonly highlighted: boolean
  readonly highlightCount: number
}) {
  const element = useRef<HTMLLIElement>(null)

  useEffect(() => {
    if (highlighted) {
      element.current?.scrollIntoView({ block: 'center', behavior: 'smooth' })
    }
  }, [highlighted, highlightCount])

  const classes = ['message', `message--${message.direction}`]
  if (highlighted) classes.push('message--highlighted')
  if (message.deleted) classes.push('message--deleted')

  return (
    <li ref={element} className={classes.join(' ')} id={`message-${message.id}`}>
      <div className="message__meta">
        <span className="message__who">{directionLabel(message.direction)}</span>
        <span className="message__time">{shortTime(message.sentAt ?? message.receivedAt)}</span>
        {message.language === undefined ? null : (
          <span className="message__flag">{message.language}</span>
        )}
        {/*
          An edited message no longer says what a draft may have been composed
          from, and a deleted one is still worth reading: both are marked rather
          than hidden.
        */}
        {message.edits > 0 ? (
          <span className="message__flag">{t('thread.edited', { count: message.edits })}</span>
        ) : null}
        {message.attachments > 0 ? (
          <span className="message__flag">{t('thread.attachments', { count: message.attachments })}</span>
        ) : null}
        {message.deleted ? <span className="message__flag message__flag--warn">{t('thread.deleted')}</span> : null}
        {message.direction === 'client_inbound' && !message.processed ? (
          <span className="message__flag message__flag--warn">{t('thread.notProcessed')}</span>
        ) : null}
      </div>
      <div className="message__text">{message.text}</div>
    </li>
  )
}
